import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { Megaphone, Save, Send } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { Button } from "@/components/button"
import { CardActionBar } from "@/components/shared/card-action-bar"
import { CardHeading } from "@/components/shared/card-heading"
import { FadeIn } from "@/components/shared/fade-in"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  LabeledSelect,
  type LabeledSelectOption,
} from "@/components/ui/labeled-select"
import { Switch } from "@/components/ui/switch"
import { toast } from "@/components/ui/toast"
import {
  DAILY_CONTENT_KIND_VALUES,
  type DailyContentKind,
} from "@/core/daily-content/kinds"
import { refreshProfiles } from "@/hooks/queries/profile.queries"
import {
  dailyContentApi,
  type DailyContentProfile,
} from "@/lib/daily-content-api"
import { dailyContentKeys } from "@/lib/query-keys"
import {
  useIsOffline,
  useProfileDraft,
  type DailyContentUiStore,
  type ProfileDraft,
} from "@/lib/stores/daily-content-ui-store"

const KIND_OPTIONS: readonly LabeledSelectOption<DailyContentKind>[] =
  DAILY_CONTENT_KIND_VALUES.map((kind) => ({
    value: kind,
    label: kind.charAt(0).toUpperCase() + kind.slice(1),
  }))

const FALLBACK_OPTIONS: readonly LabeledSelectOption<"none" | "preset">[] = [
  { value: "none", label: "None" },
  { value: "preset", label: "Preset" },
]

type ProfileCardProps = {
  profile: DailyContentProfile
  store: DailyContentUiStore
}

function useCanShowSuccessToast() {
  const router = useRouter()
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  return useCallback(() => {
    const pathname = router.state.location.pathname
    return (
      isMounted.current &&
      (pathname === "/daily-content" || pathname === "/daily-content/")
    )
  }, [router])
}

export function ProfileCard({ profile, store }: ProfileCardProps) {
  const queryClient = useQueryClient()
  const canShowSuccessToast = useCanShowSuccessToast()
  const draft = useProfileDraft(store, profile.id)
  const isOffline = useIsOffline(store)

  const isDraftDirty = useMemo(
    () =>
      !draft ||
      draft.name !== profile.name ||
      draft.kind !== profile.kind ||
      draft.targetChannelId !== profile.targetChannelId ||
      draft.scheduleValue !== profile.scheduleValue ||
      draft.fallbackMode !== profile.fallbackMode ||
      draft.pingRoleId !== (profile.pingRoleId ?? "") ||
      draft.pinMessage !== profile.pinMessage ||
      draft.createThread !== profile.createThread,
    [draft, profile],
  )

  const updateProfileMutation = useMutation({
    mutationFn: (input: { profileId: string; draft: ProfileDraft }) =>
      dailyContentApi.updateProfile(input.profileId, {
        name: input.draft.name,
        kind: input.draft.kind,
        targetChannelId: input.draft.targetChannelId,
        scheduleValue: input.draft.scheduleValue,
        fallbackMode: input.draft.fallbackMode,
        pingRoleId: input.draft.pingRoleId || null,
        pinMessage: input.draft.pinMessage,
        createThread: input.draft.createThread,
      }),
    onSuccess: async () => {
      store.getState().setPageError(null)
      if (canShowSuccessToast()) {
        toast.success("Profile updated")
      }
      await refreshProfiles(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setPageError(
          error instanceof Error ? error.message : "Failed to update profile",
        )
    },
  })

  const pauseMutation = useMutation({
    mutationFn: (profileId: string) => dailyContentApi.pauseProfile(profileId),
    onSuccess: async () => {
      store.getState().setPageError(null)
      if (canShowSuccessToast()) {
        toast.success("Profile paused")
      }
      await refreshProfiles(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setPageError(
          error instanceof Error ? error.message : "Failed to pause profile",
        )
    },
  })

  const resumeMutation = useMutation({
    mutationFn: (profileId: string) => dailyContentApi.resumeProfile(profileId),
    onSuccess: async () => {
      store.getState().setPageError(null)
      if (canShowSuccessToast()) {
        toast.success("Profile resumed")
      }
      await refreshProfiles(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setPageError(
          error instanceof Error ? error.message : "Failed to resume profile",
        )
    },
  })

  const publishNowMutation = useMutation({
    mutationFn: (profileId: string) => dailyContentApi.publishNow(profileId),
    onSuccess: () => {
      store.getState().setPageError(null)
      if (canShowSuccessToast()) {
        toast.success("Profile published")
      }
    },
    onError: (error) => {
      store
        .getState()
        .setPageError(
          error instanceof Error ? error.message : "Failed to publish profile",
        )
    },
  })

  const updateField = useCallback(
    <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
      if (!draft) return
      store.getState().updateProfileDraftField(profile.id, key, value)
    },
    [store, profile.id, draft],
  )

  if (!draft) {
    return null
  }

  return (
    <FadeIn>
      <Card className="transition-shadow duration-200 hover:shadow-lg hover:shadow-primary/8">
        <CardHeader>
          <CardHeading
            icon={<Megaphone className="size-4" />}
            className="inline-flex max-w-full"
          >
            <span className="truncate" title={profile.name}>
              {profile.name}
            </span>
          </CardHeading>
          <CardDescription>
            {profile.kind} · {profile.isActive ? "active" : "paused"}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input
              value={draft.name}
              onChange={(event) => {
                updateField("name", event.target.value)
              }}
            />
          </div>
          <LabeledSelect
            label="Kind"
            value={draft.kind}
            options={KIND_OPTIONS}
            onValueChange={(kind) => {
              updateField("kind", kind)
            }}
          />
          <div className="grid gap-1.5">
            <Label>Target Channel ID</Label>
            <Input
              value={draft.targetChannelId}
              onChange={(event) => {
                updateField("targetChannelId", event.target.value)
              }}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Cron (UTC)</Label>
            <Input
              value={draft.scheduleValue}
              onChange={(event) => {
                updateField("scheduleValue", event.target.value)
              }}
            />
          </div>
          <LabeledSelect
            label="Fallback Mode"
            value={draft.fallbackMode}
            options={FALLBACK_OPTIONS}
            onValueChange={(fallbackMode) => {
              updateField("fallbackMode", fallbackMode)
            }}
          />
          <div className="grid gap-1.5">
            <Label>Ping Role ID</Label>
            <Input
              value={draft.pingRoleId}
              onChange={(event) => {
                updateField("pingRoleId", event.target.value)
              }}
              placeholder="Optional"
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs tracking-wide text-muted-foreground uppercase">
              Message Options
            </Label>
            <div className="inline-flex items-center gap-2 text-sm">
              <Switch
                id={`profile-${profile.id}-pin-message`}
                checked={draft.pinMessage}
                onCheckedChange={(checked) => {
                  updateField("pinMessage", checked)
                }}
              />
              <Label
                htmlFor={`profile-${profile.id}-pin-message`}
                className="text-sm font-normal"
              >
                Pin published message
              </Label>
            </div>
            <div className="inline-flex items-center gap-2 text-sm">
              <Switch
                id={`profile-${profile.id}-create-thread`}
                checked={draft.createThread}
                onCheckedChange={(checked) => {
                  updateField("createThread", checked)
                }}
              />
              <Label
                htmlFor={`profile-${profile.id}-create-thread`}
                className="text-sm font-normal"
              >
                Create follow-up thread
              </Label>
            </div>
          </div>

          <CardActionBar className="md:col-span-2 xl:col-span-3">
            <Button
              variant="outline"
              disabled={
                !isDraftDirty || updateProfileMutation.isPending || isOffline
              }
              onClick={() => {
                updateProfileMutation.mutate({
                  profileId: profile.id,
                  draft,
                })
              }}
            >
              <Save className="size-4" />
              Save
            </Button>
            {profile.isActive ? (
              <Button
                variant="outline"
                disabled={pauseMutation.isPending || isOffline}
                onClick={() => {
                  pauseMutation.mutate(profile.id)
                }}
              >
                Pause
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={resumeMutation.isPending || isOffline}
                onClick={() => {
                  resumeMutation.mutate(profile.id)
                }}
              >
                Resume
              </Button>
            )}
            <Button
              variant="outline"
              disabled={publishNowMutation.isPending || isOffline}
              onClick={() => {
                publishNowMutation.mutate(profile.id)
              }}
            >
              <Send className="size-4" />
              Publish Now
            </Button>
          </CardActionBar>
        </CardContent>
      </Card>
    </FadeIn>
  )
}
